import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, InputAdornment, IconButton, Tooltip, Stack, CircularProgress, Typography } from "@mui/material";
import { Search, Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslate } from "src/locales";
import { referenceService } from 'src/api/references/referenceService';
import { GetPatientProcessResponseDto } from "src/api/references/referenceServicesDtos";
import { dateTimeFormatter } from "src/utils/date";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useGridPrint } from "src/hooks/useGridPrint";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import { GridColDef, GridRenderCellParams, GridToolbarContainer, GridSortModel } from "@mui/x-data-grid";
import ByzDataTreeListGrid from "src/components/core-components/mui/data-grid/ByzDataTreeListGrid";


interface PatientProcessProps {
    open: boolean;
    onClose: () => void;
    ptId?: string;
    upn?: string;
}

interface ProcessRow {
    id: number;
    parentId: number | null;
    processName: string;
    categoryName: string;
    processTime: string;
    officialCode: string;
    doctorName: string;
    quantity: number;
    processTypeName: string;
    creatorUser: string;
}

const ProcessCustomToolbar = ({ searchTerm, onSearchChange, onPrint }: {
    searchTerm: string;
    onSearchChange: (val: string) => void;
    onPrint: () => void;
}) => {
    const { t } = useTranslate();

    return (
        <GridToolbarContainer sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ByzMuiTextField
                placeholder={t("patient-process.search-process")}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                size="small"
                autoComplete="off"
                sx={{
                    width: 300,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '24px',
                        backgroundColor: '#f8fafc',
                        '& fieldset': {
                            border: 'none',
                        },
                        '&:hover fieldset': {
                            border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                            border: 'none',
                        },
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Search sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                    ),
                }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ height: 40, width: 1, borderLeft: '1px solid #000', mx: 2 }} />
                <ByzMuiPrintButton
                    type={1}
                    onClick={onPrint}
                    sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        p: 0,
                        '& img': {
                            width: '24px !important',
                            height: '24px !important',
                            maxWidth: 'none !important',
                            maxHeight: 'none !important'
                        }
                    }}
                />
            </Stack>
        </GridToolbarContainer>
    );
}

// Helper to reconstruct tree from flat rows
const buildTree = (flatRows: ProcessRow[]) => {
    const tree: any[] = [];
    const map = new Map<number, any>();

    // First pass: create nodes
    flatRows.forEach(row => {
        map.set(row.id, { ...row, children: [] });
    });

    // Second pass: link parent/child
    flatRows.forEach(row => {
        const node = map.get(row.id);
        if (row.parentId && map.has(row.parentId)) {
            map.get(row.parentId).children.push(node);
        } else {
            tree.push(node);
        }
    });

    return tree;
};

// Helper to flatten tree back to rows
const flattenTree = (nodes: any[], result: ProcessRow[] = []) => {
    nodes.forEach(node => {
        const { children, ...row } = node;
        result.push(row);
        if (children && children.length > 0) {
            flattenTree(children, result);
        }
    });
    return result;
};

export const PatientProcess = (props: PatientProcessProps) => {
    const [rows, setRows] = useState<ProcessRow[]>([]);
    const [loading, setLoading] = useState(false); // Process loading
    const [patientLoading, setPatientLoading] = useState(!!(props.open && props.ptId)); // Patient data loading
    const { t } = useTranslate();

    const sortTreeData = useCallback((data: ProcessRow[], sortModel: GridSortModel) => {
        if (!sortModel || sortModel.length === 0) return data;

        const { field, sort } = sortModel[0];
        if (!sort) return data;

        const tree = buildTree(data);

        const sortRecursive = (nodes: any[]) => {
            nodes.sort((a: any, b: any) => {
                const valA = a[field] || "";
                const valB = b[field] || "";

                if (valA < valB) return sort === 'asc' ? -1 : 1;
                if (valA > valB) return sort === 'asc' ? 1 : -1;
                return 0;
            });

            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    sortRecursive(node.children);
                }
            });
        };

        sortRecursive(tree);
        return flattenTree(tree);

    }, []);

    const handleSortModelChange = useCallback((model: GridSortModel) => {
        setRows(prevRows => sortTreeData([...prevRows], model));
    }, [sortTreeData]);

    const fetchPatientProcesses = useCallback(async () => {
        if (!props.ptId) return;

        setLoading(true);
        try {
            const response = await referenceService.patientProcess.getPatientProcessList({
                PtId: Number(props.ptId)
            });

            if (!response?.data || response.data.length === 0) {
                setRows([]);
                return;
            }

            // Recursive function to sort nodes by processTime ascending initially
            const sortNodes = (nodes: GetPatientProcessResponseDto[]) => {
                nodes.sort((a, b) => {
                    const timeA = a.processtime || "";
                    const timeB = b.processtime || "";
                    if (timeA < timeB) return -1;
                    if (timeA > timeB) return 1;
                    return 0;
                });

                nodes.forEach(node => {
                    if (node.children?.length > 0) {
                        sortNodes(node.children);
                    }
                });
            };

            // Initial sort ASC
            sortNodes(response.data);

            const flattenedRows: ProcessRow[] = [];

            const flatten = (items: GetPatientProcessResponseDto[]) => {
                items.forEach((item) => {
                    flattenedRows.push({
                        id: item.id,
                        parentId: item.parentid,
                        processName: item.processname || "",
                        categoryName: item.categoryname || "",
                        processTime: item.processtime ? dateTimeFormatter(item.processtime) : "",
                        officialCode: item.officialcode || "",
                        doctorName: item.doctorname || "",
                        quantity: item.quantity,
                        processTypeName: item.processtypename || "",
                        creatorUser: item.creatoruser,
                    });
                    if (item.children && item.children.length > 0) {
                        flatten(item.children);
                    }
                });
            };

            flatten(response.data);
            setRows(flattenedRows);
        } catch (error) {
            console.error("Error fetching patient processes:", error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [props.ptId]);

    const handleDeleteProcess = useCallback(async (id: number) => {
        if (!window.confirm(t("delete-confirm.title"))) return;

        try {
            await referenceService.patientProcess.deletePatientProcess(id);
            fetchPatientProcesses();
        } catch (error) {
            console.error("Error deleting process:", error);
        }
    }, [t, fetchPatientProcesses]);

    const columns: GridColDef[] = useMemo(() => [
        { field: "tree_indicator", headerName: "", width: 60, sortable: false, filterable: false },
        { field: "processTime", headerName: t("patient-process.process-time"), flex: 1 },
        { field: "categoryName", headerName: t("patient-process.category-name"), flex: 1.2 },
        {
            field: "processName",
            headerName: t("patient-process.process-name"),
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    component="span"
                    sx={{
                        fontWeight: (params.row.parentId === null || params.row.parentId === undefined) ? 'bold' : 'normal',
                    }}
                >
                    {params.value}
                </Box>
            )
        },
        { field: "officialCode", headerName: t("patient-process.official-code"), flex: 1 },
        { field: "doctorName", headerName: t("patient-process.doctor-name"), flex: 1.5 },
        { field: "quantity", headerName: t("patient-process.quantity"), flex: 0.5, type: 'number' },
        { field: "processTypeName", headerName: t("patient-process.process-type"), flex: 1 },
        { field: "creatorUser", headerName: t("patient-process.creator-user"), flex: 1 },
        {
            field: "delete",
            headerName: t("delete"),
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    onClick={() => handleDeleteProcess(params.row.id)}
                    color="error"
                    size="small"
                >
                    <Tooltip title={t("delete")}>
                        <DeleteIcon fontSize="small" />
                    </Tooltip>
                </IconButton>
            )
        }
    ], [t, handleDeleteProcess]);

    useEffect(() => {
        if (props.open && props.ptId) {
            setPatientLoading(true); // Assume patient loading starts
            fetchPatientProcesses();
        }

        // Clean up on close to prevent stale data
        if (!props.open) {
            setRows([]);
            setPatientLoading(false);
        }
    }, [props.open, props.ptId, fetchPatientProcesses]);

    const { printGrid } = useGridPrint();

    const handlePatientLoadingChange = useCallback((isLoading: boolean) => {
        setPatientLoading(isLoading);
    }, []);

    const handlePrint = useCallback(() => {
        printGrid({
            rows: rows,
            columns: columns,
            title: t("patient-process.title"),
            idField: "id",
            parentIdField: "parentId",
            indentColumn: "processName"
        });
    }, [rows, columns, t, printGrid]);

    const ToolbarComp = useCallback((props: any) => (
        <ProcessCustomToolbar {...props} onPrint={handlePrint} />
    ), [handlePrint]);

    const isPageLoading = loading || patientLoading;

    return (
        <OpenComponentDialog
            title={t("patient-process.title")}
            open={props.open}
            onClose={props.onClose}
            isMedCard={true}
            isOpenMedCard={false}
            rowID={props.ptId}
            showPatientData={false} // Disable default patient data to manage manually
        >
            <Box sx={{ height: '75vh', width: '100%', p: 0, position: 'relative' }}>
                {isPageLoading && (
                    <Stack
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            height: '100%',
                            width: '100%',
                            gap: 2,
                            position: 'absolute',
                            zIndex: 10,
                            backgroundColor: 'white'
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t("loading")}
                        </Typography>
                    </Stack>
                )}

                <Box sx={{ visibility: isPageLoading ? 'hidden' : 'visible', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <PatientDatas
                        patientId={props.ptId}
                        isOpenMedCard={true}
                        isCreateMedCard={true}
                        onLoadingChange={handlePatientLoadingChange}
                    />
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <ByzDataTreeListGrid
                            initialData={rows}
                            columns={columns}
                            loading={false}
                            treeColumn="tree_indicator"
                            indentColumns={['processTime']}
                            nameField="processName"
                            idField="id"
                            parentIdField="parentId"
                            autoHeight={false}
                            whiteGrid={true}
                            pagination={true}
                            showDefaultToolbarButtons={false}
                            useLConnector={true}
                            useSubdirectoryArrow={false}
                            sortingMode="server"
                            onSortModelChange={handleSortModelChange}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'processTime', sort: 'asc' }],
                                },
                            }}
                            customToolbar={ToolbarComp}
                            sx={{ height: '100%' }}
                        />
                    </Box>
                </Box>
            </Box>
        </OpenComponentDialog>
    );
};

export default PatientProcess;
